/* The gateway's Odoo client API (bff/src/derp_bff/views_api.py): menus, actions, views, buttons, records. */
import { api, erpUrl } from '@/modules/erp/api/client'
import type { Rec, Spec } from '../engine/api'

export type { Rec, Spec }
export type Domain = unknown[]
export type Ctx = Record<string, unknown>

export interface FieldInfo {
    name: string
    type: string
    string: string
    relation?: string
    relation_field?: string
    required?: boolean
    readonly?: boolean
    selection?: [string, string][]
    domain?: string | Domain
    context?: string | Ctx
    currency_field?: string
    aggregator?: string
    store?: boolean
    sortable?: boolean
    groupable?: boolean
    help?: string
}
export type Fields = Record<string, FieldInfo>

export interface ToolbarItem {
    id: number
    name: string
    binding_view_types?: string
    domain?: string
}

export interface ViewInfo {
    arch: string
    id: number | false
    model: string
    toolbar?: { print?: ToolbarItem[]; action?: ToolbarItem[] }
    filters?: { id: number; name: string; domain: string; context: string; is_default: boolean; user_ids?: number[] }[]
}

export interface Views {
    views: Partial<Record<'list' | 'form' | 'search' | 'kanban' | 'calendar' | 'graph' | 'pivot', ViewInfo>>
    models: Record<string, { fields: Fields }>
}

export interface Action {
    id?: number
    type: string
    name?: string
    res_model?: string
    res_id?: number | false
    domain?: string | Domain | false
    context?: string | Ctx
    views?: [number | false, string][]
    view_mode?: string
    target?: string
    search_view_id?: [number, string] | false
    limit?: number
    help?: string | false
    report_name?: string
    report_type?: string
    tag?: string
    params?: Ctx
    url?: string
    /** Readable URL path (Odoo 17+), e.g. "customer-invoices". */
    path?: string | false
}

export interface MenuNode {
    id: number
    name: string
    xmlid: string | false
    action_id: number | false
    action_model: string | false
    /** The action's readable path, when it has one. */
    path?: string | false
    children: MenuNode[]
}

export interface Group {
    __count: number
    __extra_domain: Domain
    [key: string]: unknown
}

const data = (model: string, op: string) => `/api/odoo/data/${model}/${op}`

export const odoo = {
    /**
     * is_admin: in Odoo's Settings group, so Seaded › Rakendused (/apps) and › Kasutajad (/users) are theirs;
     * reports: the web app's report pages the person may open (overview, trial_balance, statements).
     */
    session: () => api.get<{ uid: number; company_id: number; context: Ctx; is_admin: boolean; reports: string[] }>('/api/odoo/session'),
    menus: () => api.get<{ apps: MenuNode[] }>('/api/odoo/menus'),
    action: (actionId: number | string, context?: Ctx) => api.post<Action>('/api/odoo/action', { action_id: actionId, context }),
    runAction: (actionId: number, context?: Ctx) => api.post<Action | false>('/api/odoo/action/run', { action_id: actionId, context }),
    views: (model: string, views: [number | false, string][], actionId?: number, context?: Ctx) =>
        api.post<Views>(`/api/odoo/views/${model}`, { views, action_id: actionId ?? null, context }),
    formView: (model: string, id: number) => api.get<number | false>(`/api/odoo/formview/${model}/${id}`),
    button: (model: string, method: string, ids: number[], context?: Ctx) =>
        api.post<{ action: Action | null }>(`/api/odoo/button/${model}`, { method, ids, context }),
    search: (model: string, body: { domain?: Domain; specification: Spec; offset?: number; limit?: number; order?: string; count_limit?: number; context?: Ctx }, signal?: AbortSignal) =>
        api.post<{ length: number; records: Rec[] }>(data(model, 'search'), body, { signal }),
    group: (model: string, body: { domain?: Domain; groupby: string[]; aggregates?: string[]; limit?: number; offset?: number; order?: string; context?: Ctx }, signal?: AbortSignal) =>
        api.post<{ groups: Group[]; length: number }>(data(model, 'group'), body, { signal }),
    /** Totals per combination of groupings (formatted_read_group): one flat row per combination. */
    aggregate: (model: string, body: { domain?: Domain; groupby: string[]; aggregates: string[]; order?: string; limit?: number; context?: Ctx }, signal?: AbortSignal) =>
        api.post<Rec[]>(data(model, 'aggregate'), body, { signal }),
    count: (model: string, domain: Domain, context?: Ctx, signal?: AbortSignal) => api.post<number>(data(model, 'count'), { domain, context }, { signal }),
    read: (model: string, ids: number[], specification: Spec, context?: Ctx) => api.post<Rec[]>(data(model, 'read'), { ids, specification, context }),
    save: (model: string, id: number | undefined, values: Rec, specification: Spec, context?: Ctx) =>
        api.post<Rec>(data(model, 'save'), { id: id ?? null, values, specification, context }),
    copy: (model: string, ids: number[], context?: Ctx) => api.post<number[]>(data(model, 'copy'), { ids, context }),
    remove: (model: string, ids: number[], context?: Ctx) => api.post<void>(data(model, 'delete'), { ids, context }),
    onchange: (model: string, body: { id?: number; values: Rec; field_names: string[]; specification: Spec; context?: Ctx }) =>
        api.post<{ value?: Rec; warning?: { title?: string; message?: string } }>(data(model, 'onchange'), { ...body, id: body.id ?? null }),
    nameSearch: (model: string, name: string, domain: Domain = [], limit = 8, context?: Ctx) =>
        api.post<{ id: number; display_name: string }[]>(data(model, 'name_search'), { name, domain, limit, context }),
    /** A record's chatter (bff thread_api.py): what needs Odoo's own methods; reading goes through search. */
    thread: {
        post: (model: string, id: number, body: string, note: boolean) => api.post<{ ok: boolean }>(`/api/odoo/thread/${model}/${id}/post`, { body, note }),
        schedule: (model: string, id: number, values: { activity_type_id?: number | null; summary?: string; note?: string; date_deadline: string; user_id?: number | null }) =>
            api.post<{ ids: number[] }>(`/api/odoo/thread/${model}/${id}/activity`, values),
        follow: (model: string, id: number, follow: boolean) => api.post<{ following: boolean }>(`/api/odoo/thread/${model}/${id}/follow`, { follow }),
        done: (activityId: number, feedback = '') => api.post<{ ok: boolean }>(`/api/odoo/activity/${activityId}/done`, { feedback }),
    },
    attachmentUrl: (id: number) => erpUrl(`/api/odoo/attachment/${id}`),
    reportUrl: (report: string, ids: number[]) => erpUrl(`/api/odoo/report/${report}/${ids.join(',')}`),
    /** A report printed from data rather than records (Odoo's pricelist report). */
    reportDataUrl: (report: string, data: Record<string, unknown>) => erpUrl(`/api/odoo/report/${report}?options=${encodeURIComponent(JSON.stringify(data))}`),
    imageUrl: (model: string, id: number, field: string, unique?: string) => erpUrl(`/api/odoo/image/${model}/${id}/${field}${unique ? `?unique=${encodeURIComponent(unique)}` : ''}`),
}
